import React, { useMemo } from 'react';
import { Trophy } from 'lucide-react';
import type { SetScore, Side } from '../../../../services/tennis/scoring';
import type { TennisMatchFormat } from '../../../../data/tennisMatchFormats';
import type { QueuedPoint } from '../../../../services/tennis/offlineQueue';
import { projectTennisMatchStats } from '../../../../services/tennis/statsProjection';
import { calculateTennisStats } from '../../../../utils/statCalculations';
import { TennisStatCategorySections } from '../stats/TennisStatTiles';

interface MatchSummaryOverlayProps {
  winner: Side;
  sets: SetScore[];
  ourName: string;
  opponentName: string;
  points: QueuedPoint[];
  format: TennisMatchFormat;
  isDoubles: boolean;
  ourPlayers: Array<{ id: string; firstName: string; lastName: string }>;
  onDone: () => void;
}

export const MatchSummaryOverlay: React.FC<MatchSummaryOverlayProps> = ({
  winner,
  sets,
  ourName,
  opponentName,
  points,
  format,
  isDoubles,
  ourPlayers,
  onDone,
}) => {
  const wonLabel = winner === 'us' ? ourName : opponentName;

  const projection = useMemo(
    () => projectTennisMatchStats({ format, points, isDoubles, participantPlayerIds: ourPlayers.map((p) => p.id) }),
    [format, points, isDoubles, ourPlayers]
  );

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto p-6 text-center">
        <Trophy className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">{wonLabel} wins</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Match complete</p>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-5">
          <div className="flex divide-x divide-gray-200 dark:divide-gray-700 bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            <div className="flex-1 py-1.5 px-2 text-left">&nbsp;</div>
            {sets.map((_, i) => (
              <div key={i} className="w-10 py-1.5 text-center">
                S{i + 1}
              </div>
            ))}
          </div>
          <div className="flex divide-x divide-gray-200 dark:divide-gray-700 border-t border-gray-200 dark:border-gray-700">
            <div className="flex-1 py-2 px-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{ourName}</div>
            {sets.map((s, i) => (
              <div key={i} className={`w-10 py-2 text-center text-sm tabular-nums ${s.ourGames > s.theirGames ? 'font-bold text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-500'}`}>
                {s.ourGames}
                {s.tiebreak && <sup className="text-[10px] ml-0.5">{s.tiebreak.ours}</sup>}
              </div>
            ))}
          </div>
          <div className="flex divide-x divide-gray-200 dark:divide-gray-700 border-t border-gray-200 dark:border-gray-700">
            <div className="flex-1 py-2 px-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{opponentName}</div>
            {sets.map((s, i) => (
              <div key={i} className={`w-10 py-2 text-center text-sm tabular-nums ${s.theirGames > s.ourGames ? 'font-bold text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-500'}`}>
                {s.theirGames}
                {s.tiebreak && <sup className="text-[10px] ml-0.5">{s.tiebreak.theirs}</sup>}
              </div>
            ))}
          </div>
        </div>

        {points.length > 0 && (
          <div className="space-y-4 mb-5 text-left">
            {isDoubles
              ? ourPlayers.map((p) => {
                  const totals = projection[p.id] ?? {};
                  const calc = calculateTennisStats(Object.entries(totals).map(([stat_key, stat_value]) => ({ stat_key, stat_value })));
                  return (
                    <div key={p.id}>
                      <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">{p.firstName} {p.lastName}</h3>
                      <TennisStatCategorySections totals={totals} calc={calc} />
                    </div>
                  );
                })
              : (() => {
                  const totals = projection[ourPlayers[0]?.id] ?? {};
                  const calc = calculateTennisStats(Object.entries(totals).map(([stat_key, stat_value]) => ({ stat_key, stat_value })));
                  return <TennisStatCategorySections totals={totals} calc={calc} />;
                })()}
          </div>
        )}

        <button type="button" onClick={onDone} className="w-full min-h-[48px] btn-primary text-sm font-medium">
          Done
        </button>
      </div>
    </div>
  );
};

export default MatchSummaryOverlay;
